import React, { useState, useEffect } from 'react';
import { 
  FaBook, 
  FaChartLine, 
  FaPlus, 
  FaDownload, 
  FaUpload, 
  FaDatabase, 
  FaTrash, 
  FaClock, 
  FaSync,
  FaUser,
  FaSignInAlt,
  FaSignOutAlt,
  FaEdit,
  FaFileExport,
  FaFileImport
} from 'react-icons/fa';
import useProverbs from '../hooks/useProverbs';
import useNotification from '../hooks/useNotification';
import { useActivityService } from '../services/activityService';
import { useAuth } from '../contexts/AuthContext';
import { proverbService } from '../services/proverbService';
import RecentActivity from '../components/dashbord/RecentActivity';

const DashboardPage = ({ setActiveSection }) => {
  const { stats, loadProverbs, proverbs } = useProverbs();
  const { showNotification, showImportExportNotification } = useNotification();
  const { addActivity, getActivities, subscribe, TYPES } = useActivityService();
  const { user } = useAuth();
  const [recentActivities, setRecentActivities] = useState(getActivities());
  const [loading, setLoading] = useState(false);

  // Subscribe to activity updates
  useEffect(() => {
    const unsubscribe = subscribe((activities) => {
      setRecentActivities(activities);
    });

    return unsubscribe;
  }, [subscribe]);

  const getTimeAgo = (date) => {
    const now = new Date();
    const diffMs = now.getTime() - new Date(date).getTime();
    const diffMins = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} minutes ago`;
    if (diffHours < 24) return `${diffHours} hours ago`;
    if (diffDays === 1) return 'Yesterday';
    return `${diffDays} days ago`;
  };

  const getActivityIcon = (type) => {
    switch (type) {
      case TYPES.LOGIN:
        return <FaSignInAlt className="activity-icon login" />;
      case TYPES.LOGOUT:
        return <FaSignOutAlt className="activity-icon logout" />;
      case TYPES.PROVERB_ADD:
        return <FaPlus className="activity-icon add" />;
      case TYPES.PROVERB_EDIT:
        return <FaEdit className="activity-icon edit" />;
      case TYPES.PROVERB_DELETE:
        return <FaTrash className="activity-icon delete" />;
      case TYPES.EXPORT:
        return <FaFileExport className="activity-icon export" />;
      case TYPES.IMPORT:
        return <FaFileImport className="activity-icon import" />;
      case TYPES.USER_ADD:
      case TYPES.USER_EDIT:
      case TYPES.USER_DELETE:
        return <FaUser className="activity-icon user" />;
      case TYPES.DATABASE:
        return <FaDatabase className="activity-icon database" />;
      default:
        return <FaClock className="activity-icon system" />;
    }
  };

  const exportProverbs = async () => {
    // Show start notification
    showImportExportNotification(
      '📤 Export Starting',
      `Preparing to export ${proverbs.length} proverbs to JSON file...`,
      'info'
    );

    try {
      // Include IDs in the export to preserve them
      const proverbsWithIds = proverbs.map(proverb => ({
        id: proverb.id, // Include the ID
        chinese_proverb: proverb.chinese_proverb,
        pinyin: proverb.pinyin,
        english_translation: proverb.english_translation,
        english_explanation: proverb.english_explanation,
        english_equivalent: proverb.english_equivalent,
        french_translation: proverb.french_translation,
        french_explanation: proverb.french_explanation,
        french_equivalent: proverb.french_equivalent,
        arabic_translation: proverb.arabic_translation,
        arabic_Explanation: proverb.arabic_Explanation,
        ...proverb // Include any other fields
      }));
      
      const dataStr = JSON.stringify(proverbsWithIds, null, 2);
      const dataBlob = new Blob([dataStr], { type: 'application/json' });
      const url = URL.createObjectURL(dataBlob);
      
      const link = document.createElement('a');
      link.href = url;
      link.download = `sway-r-k-proverbs-${new Date().toISOString().split('T')[0]}.json`;
      link.click();
      
      URL.revokeObjectURL(url);
      await addActivity(TYPES.EXPORT, `Exported ${proverbs.length} proverbs to JSON file with IDs`);
      
      // Show success notification
      showImportExportNotification(
        '✅ Export Completed Successfully!',
        `Successfully exported ${proverbs.length} proverbs with all translations and metadata. The file has been downloaded to your device.`,
        'success'
      );
      
    } catch (error) {
      console.error('Export error:', error);
      showImportExportNotification(
        '❌ Export Failed',
        `Failed to export proverbs: ${error.message}`,
        'error'
      );
    }
  };

  const triggerFileInput = () => {
    const fileInput = document.createElement('input');
    fileInput.type = 'file';
    fileInput.accept = '.json';
    fileInput.style.display = 'none';
    fileInput.onchange = (event) => importProverbs(event);
    document.body.appendChild(fileInput);
    fileInput.click();
    document.body.removeChild(fileInput);
  };

  const importProverbs = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = async function(e) {
      try {
        setLoading(true);
        const importedData = JSON.parse(e.target.result);
        
        if (!Array.isArray(importedData)) {
          throw new Error('Invalid file format - expected an array');
        }

        console.log(`📁 Starting import of ${importedData.length} proverbs...`);
        
        // Show start notification
        showImportExportNotification(
          '📤 Import Starting',
          `Processing ${importedData.length} proverbs from uploaded JSON file. Please wait while we validate and import the data...`,
          'info'
        );

        const results = await proverbService.importProverbsBatch(importedData);
        await loadProverbs();
        
        console.log('📊 Import Results:', results);
        
        const message = `Import completed: ${results.successCount} added, ${results.duplicateCount} duplicates, ${results.errorCount} errors`;
        await addActivity(TYPES.IMPORT, message);
        
        // Show detailed results
        if (results.errorCount > 0) {
          showImportExportNotification(
            '⚠️ Import Completed with Issues',
            `${message}\n\nSome entries had errors. Successfully imported ${results.successCount} proverbs, found ${results.duplicateCount} duplicates, and ${results.errorCount} failed entries. Check the console for detailed error information.`,
            'warning'
          );
          console.log('❌ Import Errors:', results.errors);
        } else if (results.duplicateCount > 0) {
          showImportExportNotification(
            '✅ Import Completed Successfully',
            `${message}\n\nAll valid entries were processed successfully! ${results.successCount} new proverbs were added to your collection. ${results.duplicateCount} duplicate entries were safely skipped.`,
            'success'
          );
        } else {
          showImportExportNotification(
            '🎉 Perfect Import!',
            `${message}\n\nAll ${results.successCount} proverbs from your file were successfully imported with no duplicates or errors. Your proverb collection has been updated!`,
            'success'
          );
        }
        
        if (results.duplicateCount > 0) {
          console.log('🔄 Duplicate Details:', results.duplicateDetails);
        }
        
      } catch (error) {
        console.error('Import error:', error);
        showImportExportNotification(
          '❌ Import Failed',
          `Failed to import proverbs: ${error.message}\n\nPlease check that your file is a valid JSON array containing proverb objects with the required fields (chinese_proverb, pinyin, english_translation).`,
          'error'
        );
      } finally {
        setLoading(false);
      }
    };
    reader.readAsText(file);
  };

  const addSampleData = async () => {
    try {
      setLoading(true);
      await proverbService.initializeSampleData();
      await loadProverbs();
      await addActivity(TYPES.DATABASE, 'Sample proverbs data added to database');
      showNotification('Sample data added successfully!', 'success');
    } catch (error) {
      showNotification('Failed to add sample data', 'error');
    } finally {
      setLoading(false);
    }
  };

  const clearAllData = async () => {
    const confirmClear = window.confirm('Are you sure you want to delete ALL proverbs? This action cannot be undone!');
    if (!confirmClear) return;

    const doubleConfirm = window.prompt('Type "DELETE ALL" to confirm:');
    if (doubleConfirm !== 'DELETE ALL') {
      showNotification('Operation cancelled', 'error');
      return;
    }

    await addActivity(TYPES.DATABASE, `All proverbs data cleared by ${user?.name || user?.email || 'user'}`);
    showNotification('All proverbs have been deleted', 'success');
  };

  const refreshActivity = async () => {
    setRecentActivities(getActivities());
    await addActivity(TYPES.SYSTEM, 'Activity feed refreshed');
    showNotification('Activity refreshed', 'success');
  };

  // Navigate to Add New Proverb section
  const navigateToAddProverb = () => {
    if (setActiveSection) {
      setActiveSection('proverbs-add'); // Use a specific identifier
      showNotification('Opening Add New Proverb form...', 'info');
    }
  };

  // Debug function to check actual Firebase count
  const checkFirebaseCount = async () => {
    try {
      setLoading(true);
      console.log('🔍 Checking Firebase count...');
      
      // Force reload from Firebase
      await loadProverbs();
      
      console.log('📊 Current stats:', stats);
      console.log('📚 Current proverbs count:', proverbs.length);
      
      showNotification(`Firebase count: ${stats.total} proverbs`, 'info');
      
    } catch (error) {
      console.error('Error checking count:', error);
      showNotification(`Error checking count: ${error.message}`, 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="dashboard-overview">
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-icon"><FaBook /></div>
          <div className="stat-content">
            <h3>Total Proverbs</h3>
            <div className="stat-value">{stats.total}</div>
            <div className="stat-change"><FaChartLine /> All languages</div>
          </div>
        </div>
      </div>

      <section className="quick-actions">
        <h3>Quick Actions</h3>
        <div className="actions-grid">
          <div className="action-card" onClick={navigateToAddProverb}>
            <div className="action-icon">
              <FaPlus />
            </div>
            <div className="action-title">Add New Proverb</div>
            <div className="action-desc">Create a new proverb entry with translations</div>
          </div>
          
          <div className="action-card" onClick={exportProverbs}>
            <div className="action-icon">
              <FaDownload />
            </div>
            <div className="action-title">Export Data</div>
            <div className="action-desc">Download all proverbs as JSON</div>
          </div>
          
          <div className="action-card" onClick={triggerFileInput}>
            <div className="action-icon">
              <FaUpload />
            </div>
            <div className="action-title">Import Data</div>
            <div className="action-desc">Upload proverbs from JSON file</div>
          </div>

          <div className="action-card" onClick={checkFirebaseCount} style={{backgroundColor: 'rgba(102, 126, 234, 0.1)', borderColor: '#667eea'}}>
            <div className="action-icon">
              <FaSync />
            </div>
            <div className="action-title">Check Count</div>
            <div className="action-desc">Verify actual Firebase count</div>
          </div>
        </div>
      </section>

      {/* Recent Activities */}
      <RecentActivity limit={15} />
    </div>
  );
};

export default DashboardPage;
